// Billing is a company entitlement, separate from role/product authorization.
// Never infer payment from a Checkout return URL or expose Stripe identifiers.
export const BILLING_PLANS = Object.freeze([
  Object.freeze({ cycle: 'monthly', amount: 49900, currency: 'usd', interval: 'month' }),
  Object.freeze({ cycle: 'annual', amount: 499000, currency: 'usd', interval: 'year' }),
]);

const CYCLES = new Set(['monthly', 'annual']);
const STATUSES = new Set(['disabled', 'exempt', 'unpaid', 'pending', 'incomplete', 'active', 'canceling', 'past_due', 'grace_expired', 'expired', 'refunded', 'disputed']);
const ADMIN_ROLES = new Set(['owner', 'admin']);
const SELECTION_KEY = 'beyondleads:billing-selection:';

function timestamp(value) {
  return typeof value === 'string' && value.length <= 40 && Number.isFinite(Date.parse(value)) ? value : null;
}

export function normalizeBillingResponse(response, companyId, role) {
  const value = response?.billing;
  if (!companyId || !value || value.companyId !== companyId || typeof value.enabled !== 'boolean' || !STATUSES.has(value.status)
    || !['canCreateCampaigns', 'canResumeCampaigns', 'canManage', 'canCheckout', 'canOpenPortal'].every((key) => typeof value[key] === 'boolean')
    || !(value.accessExpiresAt === null || (typeof value.accessExpiresAt === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value.accessExpiresAt) && timestamp(value.accessExpiresAt)))) {
    throw new Error('billing_response_invalid');
  }
  if ((value.exemptionActive !== undefined && typeof value.exemptionActive !== 'boolean')
    || (value.exemptionExpiresAt !== undefined && value.exemptionExpiresAt !== null && !timestamp(value.exemptionExpiresAt))
    || (value.status === 'exempt' && (value.exemptionActive !== true || value.canCheckout))) throw new Error('billing_response_invalid');
  const canManage = ADMIN_ROLES.has(role) && value.canManage;
  const billing = {
    companyId,
    enabled: value.enabled,
    nativeEnabled: canManage && value.nativeEnabled === true,
    status: value.status,
    exemptionActive: value.exemptionActive === true,
    exemptionExpiresAt: timestamp(value.exemptionExpiresAt),
    cycle: CYCLES.has(value.cycle) ? value.cycle : null,
    currentPeriodEnd: timestamp(value.currentPeriodEnd),
    graceEndsAt: timestamp(value.graceEndsAt),
    accessExpiresAt: timestamp(value.accessExpiresAt),
    cancelAtPeriodEnd: value.cancelAtPeriodEnd === true,
    scheduledCycle: CYCLES.has(value.scheduledCycle) ? value.scheduledCycle : null,
    scheduledChangeAt: timestamp(value.scheduledChangeAt),
    canCreateCampaigns: value.canCreateCampaigns,
    canResumeCampaigns: value.canResumeCampaigns,
    canManage,
    canCheckout: canManage && value.canCheckout,
    canOpenPortal: canManage && value.canOpenPortal,
    pauseStatus: ['none', 'pending', 'failed', 'confirmed'].includes(value.pauseStatus) ? value.pauseStatus : 'none',
  };
  if (canManage && value.contact && typeof value.contact === 'object') {
    billing.contact = {
      email: typeof value.contact.email === 'string' ? value.contact.email.slice(0, 254) : '',
      phone: typeof value.contact.phone === 'string' ? value.contact.phone.slice(0, 40) : '',
    };
  }
  return Object.freeze({
    billing: Object.freeze(billing),
    // Approved plan amounts are fixed on both client and server. A malformed
    // response cannot make the UI advertise an unapproved amount.
    plans: BILLING_PLANS,
    termsVersion: typeof response.termsVersion === 'string' && /^[a-zA-Z0-9_.-]{1,100}$/.test(response.termsVersion) ? response.termsVersion : null,
  });
}

export function billingEntitlements(state, companyId, now = Date.now()) {
  if (state?.companyId !== companyId || state.status !== 'ready' || !state.snapshot?.billing) {
    return { canCreate: false, canResume: false };
  }
  const billing = state.snapshot.billing;
  if (!billing.enabled) return { canCreate: true, canResume: true };
  // Historical display dates cannot override an unenforced rollout. Only the
  // server's access deadline bounds its authoritative current permissions.
  const expired = billing.accessExpiresAt && Date.parse(billing.accessExpiresAt) <= now;
  return {
    canCreate: billing.canCreateCampaigns && !expired,
    canResume: billing.canResumeCampaigns && !expired,
  };
}

export function safeStripeRedirect(value, kind) {
  try {
    const url = new URL(value);
    const hostname = kind === 'checkout' ? 'checkout.stripe.com' : 'billing.stripe.com';
    return url.protocol === 'https:' && url.hostname === hostname && !url.username && !url.password && !url.port ? url.href : null;
  } catch { return null; }
}

export function billingErrorMessage(error) {
  if (error?.code === 'billing_subscription_required') return 'Your company needs paid subscription access before creating or resuming campaigns. View Billing to continue.';
  if (['billing_not_configured', 'billing_unavailable', 'stripe_not_configured', 'billing_disabled'].includes(error?.code)) return 'Billing is not available in this environment yet. Your company has not been charged. Contact BeyondLeads for help.';
  if (error?.status === 403) return 'Only a company owner or admin can manage this subscription.';
  if (error?.status === 409) return 'Billing has changed since this page was opened. Refresh the subscription before trying again.';
  return 'Billing could not be confirmed. Refresh and try again. Do not start another checkout while a payment is being confirmed.';
}

const DISPUTE_COPY = Object.freeze({
  title: 'Payment dispute',
  description: 'A disputed subscription payment restricts campaign creation and resumption. Contact Finance about the dispute and recovery options. The renewal grace period does not apply.',
});

// The public billing contract carries a restriction, not the provider outcome.
// Do not infer an open, won or lost dispute from subscription or invoice state.
export function billingDisputeCopy(billing) {
  return billing?.status === 'disputed' ? DISPUTE_COPY : null;
}

export function billingAccessCopy(billing, { resume = false } = {}) {
  if (!billing) return 'Checking your company’s subscription. Campaign access stays blocked until it can be verified.';
  if (billing.status === 'disputed') return billingDisputeCopy(billing).description;
  if (billing.status === 'refunded') return 'The current paid period was fully refunded. Campaign access ended immediately and renewal stopped.';
  if (billing.status === 'exempt') return 'Payment is not required for this company while its billing exemption is active. Normal campaign permissions apply. Paused ads must be explicitly resumed by an owner or admin.';
  if (resume && billing.canCreateCampaigns && !billing.canResumeCampaigns) return 'A renewal payment is overdue. You can create campaigns during the 14-day grace period, but confirmed paid access is required to resume ads.';
  if (billing.status === 'grace_expired') return 'The 14-day renewal grace period has ended. Pay the outstanding invoice to restore access. Managed ads stay paused after payment until an owner or admin explicitly resumes them.';
  if (billing.status === 'expired') return 'Your company’s paid access has ended. Subscribe again before creating or resuming campaigns.';
  if (billing.status === 'pending' || billing.status === 'incomplete') return 'Your payment is awaiting confirmation. You can browse the app; campaign creation remains blocked until payment is verified.';
  return 'Browse the app and preview ads now. Subscribe to create campaigns. There is no free plan or trial.';
}

export function rememberBillingSelection(companyId, types, storage = globalThis.sessionStorage) {
  if (!companyId) return;
  const selected = [...new Set((Array.isArray(types) ? types : []).filter((key) => typeof key === 'string' && /^[a-z][a-z0-9_]{0,63}$/.test(key)))].slice(0, 100);
  try { storage?.setItem(`${SELECTION_KEY}${companyId}`, JSON.stringify(selected)); } catch { /* optional continuity only */ }
}

export function readBillingSelection(companyId, storage = globalThis.sessionStorage) {
  if (!companyId) return [];
  try {
    const value = JSON.parse(storage?.getItem(`${SELECTION_KEY}${companyId}`) || '[]');
    return Array.isArray(value) ? [...new Set(value.filter((key) => typeof key === 'string' && /^[a-z][a-z0-9_]{0,63}$/.test(key)))].slice(0, 100) : [];
  } catch { return []; }
}

export function clearBillingSelection(companyId, storage = globalThis.sessionStorage) {
  try { storage?.removeItem(`${SELECTION_KEY}${companyId}`); } catch { /* optional continuity only */ }
}
